import {
	type AIAttachmentV1,
	AIAttachmentFileFormatV1,
	type AIDialogCurrentTaskV1,
	AIMessageRoleV1,
	AIProblemTaskTypeV1,
	type AIVisibleMessageV1,
} from '@/shared/api';

import type {
	ChatMessage,
	ChatRole,
	QuizQuestion,
	TaskFile,
} from '../model/types';

/** Языковые форматы исходного кода. Используются для определения kind=edit/view. */
const CODE_FORMATS: ReadonlySet<AIAttachmentFileFormatV1> = new Set([
	AIAttachmentFileFormatV1.Cpp,
	AIAttachmentFileFormatV1.Py,
	AIAttachmentFileFormatV1.Java,
	AIAttachmentFileFormatV1.Go,
	AIAttachmentFileFormatV1.Cs,
	AIAttachmentFileFormatV1.Js,
	AIAttachmentFileFormatV1.Sql,
]);

/** Attachment является select-опросом, если бэк прислал config с опциями. */
const isSelectAttachment = (a: AIAttachmentV1): boolean =>
	!!a.config?.options?.length;

/** Преобразует server-side роль в нашу клиентскую. */
function mapRole(role: AIMessageRoleV1): ChatRole {
	if (role === AIMessageRoleV1.Assistant) return 'interviewer';
	if (role === AIMessageRoleV1.User) return 'you';
	return 'system';
}

function formatTime(iso?: string): string {
	if (!iso) return '00:00';
	const d = new Date(iso);
	return `${String(d.getHours()).padStart(2, '0')}:${String(
		d.getMinutes(),
	).padStart(2, '0')}`;
}

const MD_NAME_RE = /\.md$/i;

/**
 * Маппит attachment в локальный TaskFile.
 *
 * Правила:
 * - fileFormat ∈ языковые → EditFile (если есть problemId или forceEditable) или ViewFile
 * - fileFormat=json/yml → MarkdownFile с raw-payload (отображаем как код)
 * - без fileFormat и имя оканчивается на .md → MarkdownFile
 * - без fileFormat иначе → TheoryFile (теоретический блок)
 *
 * `forceEditable` — для code-таски: бэк присылает редактируемый файл кандидата
 * без problemId, но редактировать его всё равно нужно.
 */
export function mapAttachmentToFile(
	att: AIAttachmentV1,
	forceEditable = false,
): TaskFile {
	const { fileName, content, fileFormat, problemId } = att;

	if (fileFormat && CODE_FORMATS.has(fileFormat)) {
		const lang = fileFormat;
		if (problemId || forceEditable) {
			return {
				kind: 'edit',
				name: fileName,
				lang,
				code: content,
				baseCode: content,
				lines: content.split('\n').length,
				version: 1,
			};
		}
		return {
			kind: 'view',
			name: fileName,
			lang,
			code: content,
			baseCode: content,
			lines: content.split('\n').length,
			version: 1,
		};
	}

	if (
		fileFormat === AIAttachmentFileFormatV1.Json ||
		fileFormat === AIAttachmentFileFormatV1.Yml
	) {
		return { kind: 'markdown', name: fileName, content };
	}

	if (MD_NAME_RE.test(fileName)) {
		return { kind: 'markdown', name: fileName, content };
	}

	return { kind: 'theory', name: fileName, content };
}

/**
 * Строит QuizQuestion из attachment.config.options (select-task).
 * Тип (single/multi) определяется taskType текущего шага диалога.
 * Текст вопроса берём из helperText/content, иначе из текста сообщения.
 */
export function tryExtractQuizFromAttachments(
	attachments: AIAttachmentV1[] | undefined,
	currentTask?: AIDialogCurrentTaskV1,
	fallbackQuestion?: string,
): QuizQuestion | null {
	if (!attachments?.length) return null;
	const selectAtt = attachments.find(isSelectAttachment);
	if (!selectAtt?.config) return null;

	const options = selectAtt.config.options.map((o) => ({
		id: o.id,
		text: o.text,
	}));
	const question =
		selectAtt.helperText?.trim() ||
		selectAtt.content?.trim() ||
		fallbackQuestion?.trim() ||
		'Выберите вариант';

	const type =
		currentTask?.taskType === AIProblemTaskTypeV1.MultiSelect
			? 'multi'
			: 'single';

	return {
		type,
		question,
		options,
		taskId: currentTask?.taskId,
	};
}

/** Маппит сообщение бэка в клиентское представление чата. */
export function mapMessageToChatMessage(
	m: AIVisibleMessageV1,
	currentTask?: AIDialogCurrentTaskV1,
): ChatMessage {
	const role = mapRole(m.role);
	// В code-таске редактируемый файл присылает интервьюер (assistant) без
	// problemId — принудительно делаем его EditFile, иначе он только для чтения.
	const forceEditable =
		role === 'interviewer' &&
		currentTask?.taskType === AIProblemTaskTypeV1.Code;
	const attaches = (m.attachments ?? [])
		// select-config attachment отображается формой квиза, а не файлом.
		.filter((a) => !isSelectAttachment(a))
		.map((a) => mapAttachmentToFile(a, forceEditable));
	const quiz =
		role === 'interviewer'
			? tryExtractQuizFromAttachments(m.attachments, currentTask, m.content)
			: null;
	return {
		role,
		time: formatTime(m.createdAt),
		text: m.content,
		messageId: m.id,
		attaches: attaches.length ? attaches : undefined,
		quiz: quiz ? { question: quiz } : undefined,
	};
}
